/* eslint-disable no-console */
import React, {
  useState,
  useContext,
  useEffect,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import qs from 'query-string';
import { openDB } from 'idb';
import { useTheme } from '@material-ui/core/styles';
import {
  Button,
  Drawer,
  IconButton,
  Typography,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import PrimaryNav from './PrimaryNav';
import SecondaryNav from './SecondaryNav';
import AppNavToggleButton from './ToggleButton';
import SimpleModal from '../SimpleModal';
import logo from '../../assets/logo/mhub_logo_desktop.svg';
import { EditIcon } from '../../assets/icons';
import EditSavedFields from '../Filter/EditSavedFields';
import LangSelector from '../LanguageLocalization';
import {
  PageContext,
  AuthContext,
} from '../../contexts';
import config from '../../config';
import { useAppNavStyles } from './styles';
import { toTitleCase } from '../../utils';
import { canManageProjectAccess, PROJECT_ACCESS_PATH } from '../../pages/Settings/ProjectAccess/access'; // TEMP Vietnam project access

const AppNav = (props) => {
  const { t } = useTranslation();

  const authContext = useContext(AuthContext);
  const {
    hasHigherAccess,
    user,
    apps,
    country,
  } = authContext.state;
  const pageContext = useContext(PageContext);
  const {
    lastReleaseNoteVersionSeen,
    navigations,
    indexedDB,
    indexedDBInfo,
    savedFields,
  } = pageContext.state;
  const {
    setWhatsNewModal,
    setIndexedDB,
    setIndexeDBInfo,
    getSavedFields,
    setCustomNavSection,
    setSavedFields,
  } = pageContext.actions;

  const classes = useAppNavStyles();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const { showMenu, toggleMenu } = props;

  const [collapse, setCollapse] = useState(false);
  const [showSubmenu, setShowSubmenu] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState('leads');
  const [activeStates, setActiveStates] = useState({
    editSavedFields: false,
    oldFilterWarning: false,
  });

  const { pathname, search } = window.location;

  // TODO: Enabled me if zendesk widget chat support is enabled again in the future
  // const handleContactUs = () => {
  //   if (!isDesktop) {
  //     toggleMenu();
  //   }
  //   openSupportWidget();
  // };

  const path = `${pathname}${search}`;
  const newReleaseNoteAvailable = (
    !lastReleaseNoteVersionSeen
    || (lastReleaseNoteVersionSeen && lastReleaseNoteVersionSeen !== config.app.version)
  );

  const data = {
    leads: [
      {
        name: 'leads',
        header: toTitleCase(t('entity.leads', 'leads')),
        items: [
          {
            text: toTitleCase(t('prefix.all', {
              entity: t('entity.leads', 'leads'),
              action: t('action.all', 'all'),
              defaultValue: '{action} {entity}',
            }, 'all leads')),
            path: '/leads?page=1',
          },
          {
            text: toTitleCase(t('prefix.my', {
              entity: t('entity.leads', 'leads'),
              action: t('action.my', 'my'),
              defaultValue: '{action} {entity}',
            }, 'my leads')),
            path: `/leads?assignee=current_user&assigned_to=${user.id}&page=1`,
          },
          {
            text: toTitleCase(t('prefix.unassigned', {
              entity: t('entity.leads', 'leads'),
              action: t('action.unassigned', 'unassigned'),
              defaultValue: '{action} {entity}',
            }, 'unassigned leads')),
            path: '/leads?assignee=unassigned&assigned_to=unassigned&page=1',
          },
          {
            text: toTitleCase(t('prefix.unqualified', {
              entity: t('entity.leads', 'leads'),
              action: t('action.unqualified', 'unqualified'),
              defaultValue: '{action} {entity}',
            }, 'unqualified leads')),
            path: '/leads?stage=unqualified&page=1',
          },
          {
            text: toTitleCase(t('prefix.deleted', {
              entity: t('entity.leads', 'leads'),
              action: t('action.deleted', 'deleted'),
              defaultValue: '{action} {entity}',
            }, 'deleted leads')),
            path: '/leads?deleted=true&page=1',
          },
          {
            text: toTitleCase(t('entity.salesperson', 'salesperson')),
            path: '/leads/salesperson',
          },
          {
            text: t('component.label.past_exports', 'Past exports'),
            path: '/leads/past_exports',
          },
        ],
      },
      ...navigations,
    ],
    customers: [
      {
        name: 'customers',
        header: toTitleCase(t('entity.customers', 'customers')),
        items: [
          {
            text: toTitleCase(t('prefix.all', {
              entity: t('entity.customers', 'customers'),
              action: t('action.all', 'all'),
              defaultValue: '{action} {entity}',
            }, 'all customers')),
            path: '/customers?page=1',
          },
          {
            hide: !!country.features.bookingConcept,
            text: t('component.label.past_imports', 'Past imports'),
            path: '/customers/past_imports',
          },
          {
            text: t('component.label.past_exports', 'Past exports'),
            path: '/customers/past_exports',
          },
        ],
      },
      ...navigations,
    ],
    opportunities: [
      {
        name: 'opportunities',
        header: country.features.bookingConcept
          ? toTitleCase(t('entity.bookings', 'bookings'))
          : toTitleCase(t('entity.opportunities', 'opportunities')),
        items: [
          {
            text: country.features.bookingConcept
              ? toTitleCase(t('prefix.all', {
                entity: t('entity.bookings', 'bookings'),
                action: t('action.all', 'all'),
                defaultValue: '{action} {entity}',
              }, 'all bookings'))
              : toTitleCase(t('prefix.all', {
                entity: t('entity.opportunities', 'opportunities'),
                action: t('action.all', 'all'),
                defaultValue: '{action} {entity}',
              }, 'all opportunities')),
            path: '/opportunities?page=1',
          },
          {
            text: country.features.bookingConcept
              ? t('component.label.ongoing_bookings', 'Ongoing bookings')
              : t('component.label.potential_opportunities', 'Potential opportunities'),
            path: country.features.bookingConcept
              ? '/opportunities?nav_param=nav_potential&stage=new,follow_up'
              : '/opportunities?nav_param=nav_potential&stage=new,follow_up,reserved,booked',
          },
          {
            hide: !!country.features.bookingConcept,
            text: country.features.bookingConcept
              ? t('component.label.cancelled_bookings', 'Cancelled bookings')
              : t('component.label.lost_opportunities', 'Lost opportunities'),
            path: '/opportunities?nav_param=nav_stage_lost&stage=lost&fields=project_name,unit_spa_value,customers,stage,lost_reason,assigned_to,created_at',
          },
          {
            text: t('component.label.appointment_calendar', 'Appointment calendar'),
            path: '/opportunities/appointment',
          },
        ],
      },
      ...navigations,
    ],
    campaigns: [
      {
        name: 'campaigns',
        header: toTitleCase(t('entity.campaigns', 'campaigns')),
        items: [
          {
            text: toTitleCase(
              t('prefix.all', {
                entity: t('entity.campaigns', 'campaigns'),
                action: t('action.all', 'all'),
                defaultValue: '{action} {entity}',
              }, 'all campaigns'),
            ),
            path: '/campaigns?page=1',
          },
          {
            hide: !hasHigherAccess,
            text: toTitleCase(t('prefix.all', {
              entity: t('entity.submissions', 'submissions'),
              action: t('action.all', 'all'),
              defaultValue: '{action} {entity}',
            }, 'all submissions')),
            path: '/campaigns/submissions?page=1',
          },
          {
            hide: !hasHigherAccess,
            text: toTitleCase(t('prefix.invalid', {
              entity: t('entity.submissions', 'submissions'),
              action: t('action.invalid', 'invalid'),
              defaultValue: '{action} {entity}',
            }, 'invalid submissions')),
            path: '/campaigns/submissions?invalid=true&page=1',
          },
          {
            hide: !hasHigherAccess,
            text: t('component.label.past_exports', 'Past exports'),
            path: '/campaigns/submissions/past_exports',
          },
        ],
      },
      {
        name: 'audiences',
        header: toTitleCase(t('entity.audiences', 'audiences')),
        hide: !hasHigherAccess,
        items: [
          {
            hide: !hasHigherAccess,
            text: toTitleCase(t('prefix.all', {
              entity: t('entity.audiences', 'audiences'),
              action: t('action.all', 'all'),
              defaultValue: '{action} {entity}',
            }, 'all audiences')),
            path: '/audiences',
          },
        ],
      },
      ...navigations,
    ],
    // chats: [
    //   {
    //     header: 'Chat',
    //     items: [
    //       {
    //         text: 'All chats',
    //         path: '/chats',
    //       },
    //     ],
    //   },
    //   ...navigations,
    // ],
    settings: [
      {
        name: 'data',
        header: t('settings.data.header', 'Data'),
        items: [
          {
            text: t('component.label.all_fields', 'All Fields'),
            path: '/settings/fields',
          },
          {
            text: t('component.label.action_status', 'Action Status'),
            path: '/settings/action_status',
          },
        ],
      },
      {
        name: 'automation',
        header: t('settings.automation.header', 'Automation'),
        items: [
          {
            text: toTitleCase(t('entity.auto_assignment', 'auto assignment')),
            path: '/settings/auto_assignment',
          },
          {
            text: t('settings.automation.workflow', 'Workflow'),
            path: '/settings/workflow',
          },
        ],
      },
      {
        name: 'campaign',
        header: toTitleCase(t('entity.campaign', 'campaign')),
        items: [
          {
            text: t('settings.campaign.api_integrations', 'API integrations'),
            path: '/settings/api_integrations',
          },
          {
            text: t('settings_calls.header.title', 'Call settings'),
            path: '/settings/call',
            hide: !(apps.lead_management_v2 && apps.lead_management_v2.hasCallAccess),
          },
          {
            text: toTitleCase(t('suffix.settings', {
              entity: t('entity.facebook', 'facebook'),
              action: t('component.text.settings', 'settings'),
              defaultValue: '{entity} {action}',
            }, 'facebook settings')),
            path: '/settings/facebook',
          },
          {
            text: t('settings.campaign.tiktok', 'TikTok settings'),
            path: '/settings/tiktok',
          },
          {
            text: t('settings.campaign.email', 'Email verification'),
            path: '/settings/email_verifications',
          },
        ],
      },
      // LMS-2843
      // {
      //   name: 'chat',
      //   header: 'Chat',
      //   items: [
      //     {
      //       text: 'Chat settings',
      //       path: '/settings/chats',
      //     },
      //   ],
      // },
    ],
    help: [
      {
        name: 'help',
        header: t('navbar.help.header', 'Help'),
        items: [
          {
            text: t('navbar.help.faq', 'FAQ'),
            path: '/faq',
          },
          {
            text: t('navbar.help.new', 'What\'s new'),
            onClick: () => { setWhatsNewModal(true); },
            haveBadge: newReleaseNoteAvailable,
          },
          {
            text: t('navbar.help.support', 'Support'),
            href: 'https://support.mhub.my/hc/en-us/categories/360001490114-Lead',
          },
          // Zendesk chat widget support has been removed since release/v1.113.0
          // {
          //   text: 'Contact us',
          //   onClick: handleContactUs,
          // },
          {
            text: t('component.label.terms', 'Terms of Use'),
            href: 'https://mhub.my/policies/terms_of_use/',
          },
        ],
      },
    ],
  };

  if (country.features.srb) {
    data.settings.push({
      name: 'srb',
      header: t('settings.srb.header', 'Showroom for Buyers'),
      items: [
        {
          text: t('settings.srb.access', 'Buyers access'),
          path: '/settings/buyer_access',
        },
      ],
    });
  }

  if (!country.features.navLeads) {
    delete data.leads;
  }

  if (!country.features.navCampaigns) {
    delete data.campaigns;
    data.settings = data.settings.filter((s) => s.name !== 'campaign');
  }

  if (country.features.bookingConcept) {
    // Vietnam: hide Data section entirely, keep only Auto Assignment in Automation
    data.settings = data.settings.filter((s) => s.name !== 'data');
    data.settings = data.settings.map((s) => {
      if (s.name === 'automation') {
        return {
          ...s,
          items: s.items.filter((item) => item.path === '/settings/auto_assignment'),
        };
      }
      return s;
    });
  }

  // TEMP Vietnam project access: HQ only, below Automation.
  if (canManageProjectAccess(user.role, country.features)) {
    data.settings.push({
      name: 'project_access',
      header: t('settings.project_access.header', 'Booking'),
      items: [{ text: t('settings.project_access.title', 'Project access'), path: PROJECT_ACCESS_PATH }],
    });
  }

  if (config.app.enableCreditCheck && user.role === 'Headquarters' && country.features.creditCheck) {
    data.settings.push({
      name: 'credit_check',
      header: toTitleCase(t('entity.credit_check', 'credit check')),
      items: [
        {
          text: toTitleCase(t('suffix.settings', {
            entity: t('entity.credit_check', 'credit check'),
            action: t('component.text.settings', 'settings'),
            defaultValue: '{entity} {action}',
          }, 'credit check settings')),
          path: '/settings/credit_check',
        },
      ],
    });
    data.customers.push({
      hide: !(config.app.enableCreditCheck && user.role === 'Headquarters'),
      name: 'credit_check',
      header: toTitleCase(t('entity.credit_check', 'credit check')),
      items: [
        {
          text: toTitleCase(t('prefix.all', {
            entity: t('entity.credit_check', 'credit check'),
            action: t('action.all', 'all'),
            defaultValue: '{action} {entity}',
          }, 'all credit check')),
          path: '/customers/credit_check',
        },
      ],
    });
  }

  // TODO: uncomment after deployment is confirmed
  if (user.role === 'Headquarters' && country.features.salescandy) {
    data.settings.push({
      name: 'salescandy',
      header: toTitleCase(t('suffix.settings', {
        entity: t('entity.salesCandy', 'salesCandy'),
        action: t('component.text.settings', 'settings'),
        defaultValue: '{entity} {action}',
      }, 'salesCandy settings')),
      items: [
        {
          text: toTitleCase(t('suffix.settings', {
            entity: t('entity.salesCandy', 'salesCandy'),
            action: t('component.text.settings', 'settings'),
            defaultValue: '{entity} {action}',
          }, 'salesCandy settings')),
          path: '/settings/salescandy',
        },
        {
          text: t('settings.salescandy.data_mapping', 'Data mapping'),
          path: '/settings/salescandy/data_mapping',
        },
      ],
    });
  }

  const toggleState = (name, state) => () => {
    setActiveStates((s) => ({ ...s, [name]: state || !s[name] }));
  };

  const getModuleName = useCallback(() => {
    const arr = pathname.split('/');
    if (arr.length < 2) {
      return '';
    }
    const name = arr[1];
    switch (name) {
      case 'audiences':
        return 'campaigns';
      default:
        return name;
    }
  }, [pathname]);

  // Will check if have old filters format
  const onSelectFilter = useCallback((e, filters) => {
    if (Array.isArray(filters)) {
      e.preventDefault();
      toggleState('oldFilterWarning')();
    } else if (!isDesktop) {
      toggleMenu();
    }
  }, [
    isDesktop,
    toggleMenu,
  ]);

  const getSavedFieldsPath = useCallback((fields, name) => {
    const encoded = qs.stringify({
      ...fields.filters,
      fields: fields.columns || [],
    }, {
      arrayFormat: 'comma',
      sort: false,
    });
    return `/${name}?${encoded}`;
  }, []);

  useEffect(() => {
    const name = isDesktop ? getModuleName() : selectedMenu;

    if (savedFields.length > 0) {
      const filterItems = savedFields.map((fields) => ({
        text: fields.name,
        path: getSavedFieldsPath(fields, name),
        onSelect: (e) => {
          onSelectFilter(e, fields.filters);
        },
      }));

      setCustomNavSection([
        {
          name: 'custom',
          header: t('appnav.saved_views', 'Saved views'),
          sideHeaderNode: (
            <IconButton
              size="small"
              onClick={() => {
                setActiveStates((s) => ({ ...s, editSavedFields: !s.editSavedFields }));
              }}
            >
              <img alt={t('component.label.edit_saved_fields', 'edit saved fields')} src={EditIcon} className={classes.editIcon} />
            </IconButton>
          ),
          items: filterItems,
        },
      ]);
    } else {
      setCustomNavSection([]);
    }
  }, [
    getSavedFieldsPath,
    onSelectFilter,
    classes,
    getModuleName,
    isDesktop,
    indexedDBInfo,
    selectedMenu,
    savedFields,
    setCustomNavSection,
    toggleMenu,
  ]);

  const updateSavedFieldsNav = useCallback(async (db, storeName) => {
    const fields = await getSavedFields(db, storeName);

    setSavedFields(fields);
  }, [
    getSavedFields,
    setSavedFields,
  ]);

  useEffect(() => {
    async function openDatabase() {
      let DB_NAME;
      let DB_STORE_NAME;
      const DB_VERSION = 2;

      const name = isDesktop ? getModuleName() : selectedMenu;

      switch (name) {
        case 'leads':
          DB_NAME = 'LeadFilters';
          DB_STORE_NAME = 'lead-filters';
          break;
        case 'customers':
          DB_NAME = 'CustomerFilters';
          DB_STORE_NAME = 'customer-filters';
          break;
        case 'opportunities':
          DB_NAME = 'OpportunityFilters';
          DB_STORE_NAME = 'opportunity-filters';
          break;
        case 'contacts':
          DB_NAME = 'ContactFilters';
          DB_STORE_NAME = 'contact-filters';
          break;
        default:
          DB_NAME = '';
          DB_STORE_NAME = '';
          break;
      }

      setIndexeDBInfo({
        name: DB_NAME,
        storeName: DB_STORE_NAME,
        version: DB_VERSION,
      });

      if (DB_NAME && DB_STORE_NAME) {
        const iDB = (
          window.indexedDB || window.mozIndexedDB || window.webkitIndexedDB || window.msIndexedDB
        );
        if (!iDB) {
          console.log("Your browser doesn't support a stable version of IndexedDB.");
        }

        const db = await openDB(DB_NAME, DB_VERSION, {
          upgrade(request) {
            if (!request.objectStoreNames.contains(DB_STORE_NAME)) {
              const store = request.createObjectStore(DB_STORE_NAME, {
                autoIncrement: true,
              });
              store.createIndex('name', 'name');
            }
          },
        });

        await updateSavedFieldsNav(db, DB_STORE_NAME);

        setIndexedDB(db);
      } else {
        setIndexedDB(null);
      }
    }
    openDatabase();
  }, [
    getModuleName,
    getSavedFields,
    isDesktop,
    updateSavedFieldsNav,
    selectedMenu,
    setIndexeDBInfo,
    setIndexedDB,
  ]);

  const handleUpdateFilters = async () => {
    await updateSavedFieldsNav(indexedDB, indexedDBInfo.storeName);
  };

  const getSecondarySelection = () => {
    let index = -1;
    let sectionName = '';
    Object.keys(data).forEach((key) => {
      const menu = data[key];
      menu.forEach((v, i) => {
        const arr = menu[i];
        if (arr.items) {
          arr.items.forEach((vv, ii) => {
            const p = arr.items[ii].path;
            if (path.startsWith(p)) {
              sectionName = arr.name;
              index = ii;
            }
          });
        }
      });
    });
    return {
      index,
      sectionName,
    };
  };

  const primaryNavIndex = getModuleName();
  const secondaryNavSelection = getSecondarySelection();

  const hasSubmenu = (name) => {
    const submenu = data[name];
    return submenu && submenu.length;
  };

  const showSecondaryNav = (
    pathname !== '/not_found'
    && hasSubmenu(getModuleName())
  );

  const onSelectMenu = (e) => {
    const { name } = e.currentTarget;
    // Reset saved fields list when navigates
    if (selectedMenu !== name) {
      setSavedFields([]);
    }
    if (hasSubmenu(name)) {
      e.preventDefault();
      setSelectedMenu(name);
      setShowSubmenu(true);
    } else {
      toggleMenu();
    }
  };

  const back = () => {
    setShowSubmenu(false);
  };

  const renderDesktopNav = () => (
    showSecondaryNav ? (
      <>
        <PrimaryNav
          selected={primaryNavIndex}
        />
        <SecondaryNav
          collapsed={collapse}
          data={data[getModuleName()]}
          selected={secondaryNavSelection}
        />
        <AppNavToggleButton
          collapsed={collapse}
          onToggle={() => setCollapse((a) => !a)}
        />
      </>
    ) : (
      <PrimaryNav
        selected={primaryNavIndex}
      />
    )
  );

  const renderDrawerNav = () => (
    <Drawer
      open={showMenu}
      onClose={toggleMenu}
    >
      <div className={classes.drawerContainer}>
        <div className={classes.drawerHeader}>
          {
            showSubmenu
              ? (
                <div
                  className={classes.controls}
                  onClick={back}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      back();
                    }
                  }}
                >
                  <ChevronLeftIcon />
                  <Typography variant="subtitle2">{t('component.label.back', 'Back')}</Typography>
                </div>
              ) : (
                <>
                  <a href="/" className={classes.logo}>
                    <img alt={t('component.alt.mhub_crm', 'MHub CRM')} src={logo} />
                  </a>
                  <LangSelector />
                </>
              )
          }
        </div>
        {
          showSubmenu
            ? (
              <SecondaryNav
                collapsed={false}
                data={data[selectedMenu]}
                selected={secondaryNavSelection}
                onSelect={toggleMenu}
              />
            ) : (
              <PrimaryNav
                selected={primaryNavIndex}
                onSelect={onSelectMenu}
              />
            )
        }
      </div>
    </Drawer>
  );

  return (
    <div className={classes.root}>
      {
        isDesktop
          ? renderDesktopNav()
          : renderDrawerNav()
      }
      <EditSavedFields
        dbStoreName={indexedDBInfo.storeName}
        open={activeStates.editSavedFields}
        database={indexedDB}
        data={savedFields}
        onClose={() => {
          setActiveStates((s) => ({ ...s, editSavedFields: !s.editSavedFields }));
        }}
        onUpdate={handleUpdateFilters}
      />
      <SimpleModal
        title={t('component.label.changes_required', 'Changes required')}
        open={activeStates.oldFilterWarning}
        onCancel={toggleState('oldFilterWarning')}
        content={(
          <Typography variant="body2">
            {t('appnav.old_filter_warning', 'We detected an outdated version of saved filters. Kindly delete the selected saved filters and save again.')}
          </Typography>
        )}
        buttons={(
          <Button
            variant="contained"
            onClick={toggleState('oldFilterWarning')}
          >
            {t('component.label.okay', 'Okay')}
          </Button>
        )}
      />
    </div>
  );
};

AppNav.Global = PrimaryNav;
AppNav.Contextual = SecondaryNav;

AppNav.propTypes = {
  showMenu: PropTypes.bool,
  toggleMenu: PropTypes.func,
};

AppNav.defaultProps = {
  showMenu: false,
  toggleMenu: () => { },
};

export default AppNav;
